import { useState, useRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, type KeyboardEvent, type ChangeEvent, type ClipboardEvent, type Ref } from 'react'
import { Plus, Square, ArrowUp, Info, Mic, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { SlashCommandPicker } from './SlashCommandPicker'
import { buildSlashItems } from './slashCommandItems'
import { ChatAttachmentChip } from './ChatAttachmentChip'
import { ChatAttachmentThumbnail } from './ChatAttachmentThumbnail'
import { ChatSkillChip } from './ChatSkillChip'
import { ChatAgentSelector } from './ChatAgentSelector'
import { CHAT_ALLOWED_EXTENSIONS, CHAT_LEGACY_OFFICE_EXTENSIONS, namePastedImage, validateChatFiles } from '@/lib/attachments'
import { readChatDraft, writeChatDraft } from '@/lib/chatDraft'
import { CHAT_AGENT_PROVIDERS } from '@/types'
import type { ChatCommandDescriptor, WorkflowDefinitionSummary, Skill } from '@/types'
import type { ChatAttachment } from '@/types/chat'
import type { ChatAgentSelectionState } from '@/hooks'

export interface ChatInputHandle {
  restoreDraft: (text: string) => boolean
}

interface ChatInputProps {
  chatId: string | null
  composerRef?: Ref<ChatInputHandle>
  isContainerMode: boolean
  containerLockedByAttachments: boolean
  onContainerModeChange: (value: boolean) => void
  onSendMessage: (message: string, useContainer: boolean) => boolean
  onCancel: () => void
  onCommandSelect: (command: ChatCommandDescriptor) => void
  onWorkflowSelect: (workflow: WorkflowDefinitionSummary) => void
  commands: ChatCommandDescriptor[]
  workflows: WorkflowDefinitionSummary[]
  skills: Skill[]
  skillsLoading?: boolean
  selectedSkills: Skill[]
  onSelectSkill: (skill: Skill) => void
  onRemoveSkill: (skillId: string) => void
  isStreaming: boolean
  isCancelling: boolean
  disabled: boolean
  attachments: ChatAttachment[]
  attachmentPreviews: Record<string, string>
  pendingUploadCount: number
  onAttachFiles: (files: File[], withPreviews?: boolean) => void
  onCancelUpload: () => void
  onRemoveAttachment: (id: string) => void
  agent: ChatAgentSelectionState
}

export function ChatInput({
  chatId,
  composerRef,
  isContainerMode,
  containerLockedByAttachments,
  onContainerModeChange,
  onSendMessage,
  onCancel,
  onCommandSelect,
  onWorkflowSelect,
  commands,
  workflows,
  skills,
  skillsLoading = false,
  selectedSkills,
  onSelectSkill,
  onRemoveSkill,
  isStreaming,
  isCancelling,
  disabled,
  attachments,
  attachmentPreviews,
  pendingUploadCount,
  onAttachFiles,
  onCancelUpload,
  onRemoveAttachment,
  agent,
}: Readonly<ChatInputProps>) {
  const [message, setMessage] = useState(() => readChatDraft(chatId))
  const [draftChatId, setDraftChatId] = useState(chatId)
  if (chatId !== draftChatId) {
    setDraftChatId(chatId)
    setMessage(readChatDraft(chatId))
  }
  const messageRef = useRef(message)
  const [slashIndex, setSlashIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pasteSequenceRef = useRef(0)
  const inFlightNamesRef = useRef<string[]>([])

  useEffect(() => {
    if (pendingUploadCount === 0) inFlightNamesRef.current = []
  }, [pendingUploadCount])

  useEffect(() => {
    writeChatDraft(chatId, message)
  }, [chatId, message])

  useLayoutEffect(() => {
    messageRef.current = message
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
  }, [message])

  useImperativeHandle(composerRef, () => ({
    restoreDraft: (text) => {
      if (messageRef.current.trim() !== '') return false
      messageRef.current = text
      setMessage(text)
      return true
    },
  }), [])

  const hasAttachments = attachments.length > 0
  const isUploading = pendingUploadCount > 0
  const attachedOrPending = attachments.length + pendingUploadCount
  const agentProvider = agent.selection?.agentProvider
  const providerForcesContainer = agent.forcesContainer
  const containerLocked = hasAttachments || providerForcesContainer || containerLockedByAttachments
  const effectiveContainerMode = isContainerMode || containerLocked

  const isSlashMode = message.startsWith('/') && !/\s/.test(message)
  const slashFilter = isSlashMode ? message.slice(1) : ''

  const slashItems = useMemo(
    () => (isSlashMode ? buildSlashItems(workflows, commands, skills, slashFilter) : []),
    [isSlashMode, workflows, commands, skills, slashFilter],
  )

  const totalItems = slashItems.length

  const canSend = (message.trim().length > 0 || attachments.length > 0 || selectedSkills.length > 0) && !isStreaming && !isUploading && !disabled && !isSlashMode

  const handleSubmit = () => {
    if (!canSend) return
    if (!onSendMessage(message.trim(), effectiveContainerMode)) return
    setMessage('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handlePickItem = useCallback(
    (index: number) => {
      const item = slashItems[index]
      if (!item) return
      if (item.type === 'workflow') onWorkflowSelect(item.workflow)
      else if (item.type === 'command') onCommandSelect(item.command)
      else onSelectSkill(item.skill)
      setMessage('')
      setSlashIndex(0)
    },
    [slashItems, onWorkflowSelect, onCommandSelect, onSelectSkill],
  )

  const handleChange = (value: string) => {
    setMessage(value)
    setSlashIndex(0)
  }

  const insertTextAtCaret = (text: string) => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.focus()
    document.execCommand('insertText', false, text)
  }

  const isNewlineShortcut = (e: KeyboardEvent<HTMLTextAreaElement>) =>
    e.key === 'Enter' && e.altKey && !e.ctrlKey && !e.metaKey

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isSlashMode && totalItems > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashIndex((prev) => (prev + 1) % totalItems)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashIndex((prev) => (prev - 1 + totalItems) % totalItems)
        return
      }
      if ((e.key === 'Enter' && !isNewlineShortcut(e)) || e.key === 'Tab') {
        e.preventDefault()
        handlePickItem(slashIndex)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMessage('')
        setSlashIndex(0)
        return
      }
    }

    if (e.key === 'Backspace' && message === '' && selectedSkills.length > 0) {
      e.preventDefault()
      onRemoveSkill(selectedSkills[selectedSkills.length - 1].id)
      return
    }

    if (isNewlineShortcut(e)) {
      e.preventDefault()
      insertTextAtCaret('\n')
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (!files.length) return

    const { valid, errors } = validateChatFiles(files, attachedOrPending)
    errors.forEach((error) => toast.error(error))

    if (valid.length > 0) {
      onAttachFiles(valid)
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (isStreaming || disabled) return

    const clipboard = e.clipboardData
    const images = Array.from(clipboard?.files ?? []).filter((file) => file.type.startsWith('image/'))
    if (images.length === 0) return

    e.preventDefault()

    const text = clipboard.getData('text/plain')
    if (text) insertTextAtCaret(text)

    const takenNames = [...attachments.map((a) => a.fileName), ...inFlightNamesRef.current]
    const named = images.map((file) => {
      const renamed = namePastedImage(file, ++pasteSequenceRef.current, takenNames)
      takenNames.push(renamed.name)
      return renamed
    })
    const { valid, errors } = validateChatFiles(named, attachedOrPending)
    errors.forEach((error) => toast.error(error))

    if (valid.length > 0) {
      inFlightNamesRef.current.push(...valid.map((file) => file.name))
      onAttachFiles(valid, true)
    }
  }

  return (
    <div className="border-t bg-card rounded-b-xl shadow-[0_0_10px_rgba(0,0,0,0.3)]">
      <div className="px-2 py-3 space-y-2 ">
        <div className="flex items-center gap-3 rounded-xl border-2 border-border p-3 bg-page">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={[...CHAT_ALLOWED_EXTENSIONS, ...CHAT_LEGACY_OFFICE_EXTENSIONS].join(',')}
            className="hidden"
            data-testid="chat-attachments-file-input"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0 border-border-accent bg-card"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming || disabled}
            aria-label="Attach files"
          >
            <Plus className="h-4 w-4" />
          </Button>

          <div className="relative flex flex-1 flex-col gap-2">
            {!effectiveContainerMode && selectedSkills.some((skill) => skill.attachmentCount > 0) && (
              <p className="text-xs text-muted-foreground" role="status" data-testid="skill-attachments-container-hint">
                Skill attachments aren&apos;t available in containerless mode — turn on Container mode to use them.
              </p>
            )}
            {(attachments.length > 0 || isUploading || selectedSkills.length > 0) && (
              <div className="flex flex-wrap gap-2" data-testid="chat-composer-attachments">
                {selectedSkills.map((skill) => (
                  <ChatSkillChip
                    key={skill.id}
                    skill={skill}
                    onRemove={() => onRemoveSkill(skill.id)}
                    disabled={isStreaming || disabled}
                  />
                ))}
                {attachments.map((a) =>
                  attachmentPreviews[a.id] ? (
                    <ChatAttachmentThumbnail
                      key={a.id}
                      attachment={a}
                      previewUrl={attachmentPreviews[a.id]}
                      onRemove={() => onRemoveAttachment(a.id)}
                    />
                  ) : (
                    <ChatAttachmentChip
                      key={a.id}
                      attachment={a}
                      onRemove={() => onRemoveAttachment(a.id)}
                    />
                  ),
                )}
                {isUploading && (
                  <span
                    className="flex min-h-[40px] items-center gap-1.5 rounded-full border bg-muted px-3 text-sm text-muted-foreground"
                    data-testid="chat-attachment-uploading"
                    role="status"
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    Uploading {pendingUploadCount === 1 ? 'file' : `${pendingUploadCount} files`}…
                    <button
                      type="button"
                      onClick={onCancelUpload}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Cancel upload"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
              </div>
            )}
            {isSlashMode && (
              <SlashCommandPicker
                items={slashItems}
                filter={slashFilter}
                selectedIndex={slashIndex}
                skillsLoading={skillsLoading}
                onSelectCommand={(cmd) => {
                  onCommandSelect(cmd)
                  setMessage('')
                  setSlashIndex(0)
                }}
                onSelectWorkflow={(wf) => {
                  onWorkflowSelect(wf)
                  setMessage('')
                  setSlashIndex(0)
                }}
                onSelectSkill={(skill) => {
                  onSelectSkill(skill)
                  setMessage('')
                  setSlashIndex(0)
                }}
              />
            )}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Continue the conversation or type / for commands"
              disabled={isStreaming || disabled}
              rows={1}
              aria-label="Chat message"
              className="w-full resize-none bg-transparent py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:opacity-50"
            />
          </div>

          <Mic className="h-4 w-4" />
          {isStreaming ? (
            <>
              <Button
                variant="destructive"
                size="icon"
                onClick={() => {
                  if (isCancelling) return
                  onCancel()
                }}
                aria-disabled={isCancelling}
                aria-busy={isCancelling}
                className={cn('shrink-0', isCancelling && 'pointer-events-none opacity-50')}
                aria-label={isCancelling ? 'Stopping response' : 'Cancel streaming'}
              >
                {isCancelling ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Square className="h-4 w-4" aria-hidden="true" />}
              </Button>
              {isCancelling && (
                <span role="status" aria-live="polite" className="sr-only">
                  Stopping response…
                </span>
              )}
            </>
          ) : (
            <Button
              size="icon"
              onClick={handleSubmit}
              disabled={!canSend}
              className="shrink-0"
              aria-label="Send message"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ChatAgentSelector
              agent={agent}
              disabled={isStreaming || disabled}
              showEffort={effectiveContainerMode || agentProvider !== CHAT_AGENT_PROVIDERS.claude}
            />
            <button
              type="button"
              role="switch"
              onClick={() => onContainerModeChange(!isContainerMode)}
              disabled={isStreaming || disabled || containerLocked}
              aria-checked={effectiveContainerMode}
              aria-label={effectiveContainerMode ? 'Container mode on' : 'Container mode off'}
              data-testid="container-toggle"
              className={cn(
                'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                effectiveContainerMode ? 'bg-accent-soft' : 'bg-muted-foreground/30',
              )}
            >
              <span
                className={cn(
                  'inline-block h-3 w-3 rounded-full bg-background shadow-sm transition-transform',
                  effectiveContainerMode ? 'translate-x-3.5' : 'translate-x-0.5',
                )}
              />
            </button>
            <span className="text-[11px] font-medium text-foreground">
              {effectiveContainerMode ? 'Container On' : 'Container Off'}
            </span>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="About container mode"
                    className="inline-flex text-muted-foreground/60 transition-colors hover:text-muted-foreground"
                  >
                    <Info className="h-3.5 w-3.5 text-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="border-transparent bg-foreground text-background text-xs">
                  {providerForcesContainer ? (
                    'Qubiz Titan always runs in a container.'
                  ) : hasAttachments ? (
                    'Container is required when files are attached.'
                  ) : containerLockedByAttachments ? (
                    'Container stays on because files were sent in this chat.'
                  ) : (
                    <div className="space-y-1">
                      <div className="whitespace-nowrap">On: the agent runs in a container with full repository access.</div>
                      <div className="whitespace-nowrap">Off: faster replies from work-item data only, no repository access.</div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  )
}
